import {
  checkMessageEn,
  checkMessageUrl,
  validateSpace,
  validateSpaceTextInput,
} from '@Utils/validation';
import { climateChangeGovernanceType } from 'src/Enum/EsgGreenhouse';
import {
  IBoardResponsibilityFormValues,
  IClimateChangeStructureFormValues,
  ISubcommitteeResponsibilityFormValues,
  ISubcommitteesFormValues,
} from '../EsgGreenhouseFormProvider/types';

export const checkClimateChangeComplete = (
  climateChangeStructure: IClimateChangeStructureFormValues,
  language: 'Th' | 'En'
) => {
  if (!climateChangeStructure) return false;

  const { boardResponsibility, subcommitteeResponsibility, committee } =
    climateChangeStructure ?? {};

  const hasBoardResponsibility = committee.boardResponsibility;
  const hasSubcommitteeResponsibility = committee.subcommitteeResponsibility;

  const isBoardComplete = Boolean(
    boardResponsibility?.[`description${language}`]
  );

  const isSubcommitteeComplete = Boolean(
    subcommitteeResponsibility?.subcommittees?.length > 0
  );

  if (hasBoardResponsibility && !hasSubcommitteeResponsibility) {
    return isBoardComplete;
  }

  if (!hasBoardResponsibility && hasSubcommitteeResponsibility) {
    return isSubcommitteeComplete;
  }

  if (hasBoardResponsibility && hasSubcommitteeResponsibility) {
    return isBoardComplete && isSubcommitteeComplete;
  }

  return false;
};

export const checkSubcommitteesComplete = (
  subcommittees: ISubcommitteesFormValues[] | undefined,
  language: 'Th' | 'En'
): boolean => {
  if (!subcommittees || subcommittees.length === 0) {
    return false;
  }

  return subcommittees.every((subcommittee) => {
    if (language === 'Th') {
      return subcommittee.responsibilitiesTh && subcommittee.subcommitteeType;
    }
    if (language === 'En') {
      return subcommittee.responsibilitiesEn && subcommittee.subcommitteeType;
    }
    return false;
  });
};

export const checkClimateChangeValidateSpace = (
  climateChangeStructure: IClimateChangeStructureFormValues,
  language: 'Th' | 'En'
) => {
  const { boardResponsibility, committee } = climateChangeStructure ?? {};

  if (!committee?.boardResponsibility) {
    return true;
  }

  const isBoardResponsibilitySpaceValid = validateSpace(
    boardResponsibility?.[`description${language}`] || ''
  );

  const isBoardResponsibilityRemarkSpaceValid = validateSpaceTextInput(
    boardResponsibility?.remark?.[`description${language}`] || ''
  );

  return (
    isBoardResponsibilitySpaceValid && isBoardResponsibilityRemarkSpaceValid
  );
};

export const checkClimateChangeValidateSpaceIntro = (
  climateChangeStructure: IClimateChangeStructureFormValues,
  language: 'Th' | 'En'
) => {
  const { introduction } = climateChangeStructure;

  const isIntroductionSpaceValid = validateSpace(
    introduction?.[`description${language}`]
  );

  return isIntroductionSpaceValid;
};

export const checkClimateChangeValidateSpaceRemark = (
  climateChangeStructure: IClimateChangeStructureFormValues,
  language: 'Th' | 'En'
) => {
  const { remark } = climateChangeStructure;

  const isRemarkSpaceValid = validateSpaceTextInput(
    remark?.[`description${language}`]
  );

  return isRemarkSpaceValid;
};

export const checkClimateChangeCompleteEnValid = (
  climateChangeStructure: IClimateChangeStructureFormValues
): boolean => {
  const { boardResponsibility } = climateChangeStructure;

  const isBoardResponsibilityEnValid = checkMessageEn(
    boardResponsibility?.descriptionEn || ''
  );

  return isBoardResponsibilityEnValid;
};

export const checkClimateChangeTermEnValid = (
  climateChangeStructure: IClimateChangeStructureFormValues
): boolean => {
  const { introduction, remark } = climateChangeStructure;

  const isIntroductionEnValid =
    !introduction?.descriptionEn || checkMessageEn(introduction.descriptionEn);

  const isRemarkEnValid =
    !remark?.descriptionEn || checkMessageEn(remark.descriptionEn);

  return isIntroductionEnValid && isRemarkEnValid;
};

export const checkClimateChangeBoardResponsibilityEnValid = (
  boardResponsibility: IBoardResponsibilityFormValues
): boolean => {
  const isDescriptionEnValid =
    !boardResponsibility?.remark?.descriptionEn ||
    checkMessageEn(boardResponsibility.remark.descriptionEn);

  return isDescriptionEnValid;
};

export const checkClimateChangeSubcommitteeResponsibilityEnValid = (
  subcommitteeResponsibility: ISubcommitteeResponsibilityFormValues
): boolean => {
  const isDescriptionEnValid =
    !subcommitteeResponsibility?.remark?.descriptionEn ||
    checkMessageEn(subcommitteeResponsibility.remark.descriptionEn);

  return isDescriptionEnValid;
};

export const checkBoardResponsibilityUrlValid = (
  boardResponsibility?: IBoardResponsibilityFormValues,
  language?: 'Th' | 'En'
): boolean => {
  const url =
    language === 'Th'
      ? boardResponsibility?.policyUrlTh
      : boardResponsibility?.policyUrlEn;
  return !url || checkMessageUrl(url);
};

export const checkHasClimateChangeValid = (
  climateChangeStructure: IClimateChangeStructureFormValues
): boolean => {
  const { committee, subcommitteeResponsibility } =
    climateChangeStructure ?? {};
  const hasSubcommitteeResponsibility = committee?.subcommitteeResponsibility;

  if (!hasSubcommitteeResponsibility) {
    return true;
  }

  return subcommitteeResponsibility?.subcommittees?.some((subcommittee) => {
    return (
      Array.isArray(subcommittee.subcommitteeRoles) &&
      subcommittee.subcommitteeRoles.some(
        (role) =>
          role.infoCategoryMasterDataId ===
          climateChangeGovernanceType.CLIMATE_CHANGE_GOVERNANCE
      )
    );
  });
};

export const checkClimateChangeSubcommitteeResponsibilityRemarkSpaceValid = (
  subcommitteeResponsibility: ISubcommitteeResponsibilityFormValues,
  language: 'Th' | 'En'
) => {
  const isSubcommitteeResponsibilityRemarkSpaceValid = validateSpaceTextInput(
    subcommitteeResponsibility?.remark?.[`description${language}`] || ''
  );

  return isSubcommitteeResponsibilityRemarkSpaceValid;
};
